import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { BankAccountsApiAdapter } from '../adapters/BankAccountsApiAdapter';
import { CreateBankAccountPayload, RejectBankAccountPayload, VerifyBankAccountPayload } from '../models/BankAccount';
import { BANK_ACCOUNTS_QUERY_KEY } from './useBankAccounts';

export const useBankAccountMutations = () => {
  const queryClient = useQueryClient();
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new BankAccountsApiAdapter(httpClient), [httpClient]);

  const invalidateBankAccounts = () => {
    queryClient.invalidateQueries({ queryKey: [BANK_ACCOUNTS_QUERY_KEY] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateBankAccountPayload) => apiAdapter.createBankAccount(payload),
    onSuccess: () => {
      invalidateBankAccounts();
    },
  });

  const verifyMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: VerifyBankAccountPayload }) =>
      apiAdapter.verifyBankAccount(id, payload),
    onSuccess: () => {
      invalidateBankAccounts();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: RejectBankAccountPayload }) =>
      apiAdapter.rejectBankAccount(id, payload),
    onSuccess: () => {
      invalidateBankAccounts();
    },
  });

  const disableMutation = useMutation({
    mutationFn: (id: number) => apiAdapter.disableBankAccount(id),
    onSuccess: () => {
      invalidateBankAccounts();
    },
  });

  return {
    createBankAccount: createMutation,
    verifyBankAccount: verifyMutation,
    rejectBankAccount: rejectMutation,
    disableBankAccount: disableMutation,
  };
};
